import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { FrontendErrorReporterService } from './frontend-error-reporter.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly reporter = inject(FrontendErrorReporterService);

  constructor() {
    this.reporter.installGlobalBrowserListeners(this);
  }

  handleError(error: unknown): void {
    const normalized = this.normalize(error);
    console.error('Unhandled frontend error:', error);
    this.reporter.report({
      kind: normalized.kind,
      message: normalized.message,
      stack: normalized.stack,
      status: normalized.status,
      details: normalized.details,
    });
  }

  private normalize(error: unknown): {
    kind: 'unhandled_error' | 'unhandled_rejection';
    message: string;
    stack?: string;
    status?: number;
    details?: Record<string, unknown>;
  } {
    const reason = (error as { reason?: unknown })?.reason ?? error;
    if (reason instanceof HttpErrorResponse) {
      return {
        kind: 'unhandled_rejection',
        message: reason.message,
        status: reason.status,
        stack: reason.error?.stack,
        details: { error: this.safeDetails(reason.error) },
      };
    }
    if (reason instanceof Error) {
      return { kind: 'unhandled_error', message: reason.message, stack: reason.stack };
    }
    return { kind: 'unhandled_error', message: typeof reason === 'string' ? reason : JSON.stringify(this.safeDetails(reason)) };
  }

  private safeDetails(value: unknown): unknown {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return String(value);
    }
  }
}
